import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

const PricingPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(null);

    const handleSubscribe = async (planId) => {
        if (!user) {
            window.location.href = '/login';
            return;
        }

        setLoading(planId);
        try {
            const response = await api.post('/payments/create-checkout-session', {
                plan_id: planId,
                success_url: `${window.location.origin}/dashboard?payment=success`,
                cancel_url: `${window.location.origin}/pricing?payment=cancelled`
            });

            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            toast.error('Error al iniciar el pago. Inténtalo de nuevo.');
            console.error(error);
        } finally {
            setLoading(null);
        }
    };

    const plans = [
        {
            id: 'basic',
            name: 'Básico',
            price: '0',
            description: 'Todo lo necesario para empezar',
            features: [
                'Hasta 50 invitados',
                '1 Plantilla estándar',
                'Confirmación RSVP vía web',
                'Mapa de ubicación',
                'Cuenta regresiva'
            ],
            cta: 'Comenzar Gratis',
            popular: false
        },
        {
            id: 'premium',
            name: 'Premium',
            price: '49.99',
            description: 'La mejor experiencia para tus invitados',
            features: [
                'Hasta 150 invitados',
                'Todas las plantillas premium',
                'Galería de fotos interactiva',
                'Música de fondo personalizada',
                'Lista de regalos / Mesa de regalos',
                'Soporte vía WhatsApp'
            ],
            cta: 'Elegir Premium',
            popular: true
        },
        {
            id: 'pro',
            name: 'Pro / Agencia',
            price: '99.99',
            description: 'Para eventos masivos y profesionales',
            features: [
                'Invitados ilimitados',
                'Subdominio personalizado',
                'Sin marca de agua de Wedvites',
                'Gestor de múltiples eventos',
                'Descarga de base de datos completa',
                'WhatsApp masivo (Próximamente)'
            ],
            cta: 'Contratar Pro',
            popular: false
        }
    ];

    return (
        <div className="min-h-screen bg-slate-50 py-20 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-5xl font-heading text-slate-900 mb-4"
                    >
                        Elige el plan perfecto para tu <span className="text-wedding-primary">gran día</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-slate-600 max-w-2xl mx-auto"
                    >
                        Precios transparentes y sin cargos ocultos. Cambia de plan en cualquier momento.
                    </motion.p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {plans.map((plan, index) => (
                        <motion.div
                            key={plan.id}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <Card className={`h-full flex flex-col relative overflow-hidden transition-all hover:shadow-xl ${plan.popular ? 'border-wedding-primary border-2' : ''}`}>
                                {plan.popular && (
                                    <div className="absolute top-0 right-0 bg-wedding-primary text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">
                                        Más Popular
                                    </div>
                                )}
                                <CardHeader>
                                    <CardTitle className="text-2xl font-heading">{plan.name}</CardTitle>
                                    <CardDescription>{plan.description}</CardDescription>
                                </CardHeader>
                                <CardContent className="flex-1">
                                    <div className="mb-6 flex items-baseline gap-1">
                                        <span className="text-4xl font-bold">${plan.price}</span>
                                        <span className="text-slate-500 text-sm">/ evento</span>
                                    </div>
                                    <ul className="space-y-3">
                                        {plan.features.map((feature, i) => (
                                            <li key={i} className="flex items-start text-sm text-slate-600">
                                                <div className="mt-1 mr-3 h-4 w-4 rounded-full bg-wedding-accent flex items-center justify-center flex-shrink-0">
                                                    <Check className="h-2.5 w-2.5 text-wedding-primary" strokeWidth={3} />
                                                </div>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                                <CardFooter>
                                    <Button
                                        className={`w-full py-6 rounded-xl ${plan.popular ? 'bg-wedding-primary hover:bg-wedding-primary-dark' : 'bg-slate-900 hover:bg-slate-800'}`}
                                        onClick={() => handleSubscribe(plan.id)}
                                        disabled={loading === plan.id}
                                    >
                                        {loading === plan.id ? 'Cargando...' : plan.cta}
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </CardFooter>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-20 text-center">
                    <p className="text-slate-500 text-sm mb-4 flex items-center justify-center gap-2">
                        <Heart className="h-4 w-4 text-wedding-primary fill-current" /> Todas las transacciones son seguras y encriptadas vía Stripe
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PricingPage;
